import { Component, Input, OnDestroy, OnInit, AfterViewInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CoreCOMService } from '../../services/com.service';
import { CoreLogService } from '../../services/log.service';
import { QRCode } from '../../forked/qr_code';
import { appConfig } from '../../core-config';

/**
 * Reusable QR Code component with automatic screen brightness adjustment for mobile devices
 *
 * Usage:
 * <qr-code-display [qrData]="qrCodeNumber" [autoActivate]="true"></qr-code-display>
 */
@Component({
  selector: 'qr-code-display',
  templateUrl: './qrCodeDisplay.component.html',
  styleUrls: ['./qrCodeDisplay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QRCodeDisplayComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() qrData: string;
  @Input() autoActivate: boolean = true;
  @Input() trigger$: Observable<boolean>;
  @Input() animate: boolean = false;

  public qrCodeSvg: SafeHtml;
  public isOnline: boolean;

  private defaultBrightness: number = 0.5;
  private readonly DEFAULT_BRIGHTNESS_FALLBACK = 0.5;
  private pauseSubscription: Subscription;
  private resumeSubscription: Subscription;
  private triggerSubscription: Subscription;
  private isActive: boolean = false;

  constructor(
    private _sanitizer: DomSanitizer,
    private _coreCOMService: CoreCOMService,
    private _coreLogService: CoreLogService,
    private _changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const me = this;
    me.isOnline = me._coreCOMService.environment.networkInformation.isOnline;
    if (me.qrData) {
      const trimmed = me.qrData.trim();
      if (trimmed) {
        me.qrCodeSvg = me.generateQRCode(trimmed);
      }
    }
  }

  async ngAfterViewInit(): Promise<void> {
    const me = this;

    // Only initialize brightness on mobile devices
    if (me._coreCOMService.environment.client.isMobile) {
      // Get current brightness to restore later
      try {
        me.defaultBrightness = await me._coreCOMService.environment.brightness.getBrightness();
      } catch (error) {
        me._coreLogService.error('QRCodeDisplayComponent.ngAfterViewInit', 'Failed to get brightness, using fallback', error);
        me.defaultBrightness = me.DEFAULT_BRIGHTNESS_FALLBACK;
      }

      // Subscribe to pause/resume events
      me.pauseSubscription = me._coreCOMService.environment.events.pause.subscribe(() => me.onPause());
      me.resumeSubscription = me._coreCOMService.environment.events.resume.subscribe(() => me.onResume());

      // Setup conditional brightness trigger if provided
      if (me.trigger$) {
        me.setupConditionalBrightness(me.trigger$);
      }
    } else {
      me.defaultBrightness = me.DEFAULT_BRIGHTNESS_FALLBACK;
    }

    me.PushChanges();
  }


  ngOnDestroy(): void {
    const me = this;
    me.deactivate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const me = this;
    if (changes['qrData']) {
      const value = me.qrData ? me.qrData.trim() : null;

      if (!value) {
        return;
      }

      me.qrCodeSvg = me.generateQRCode(value);

      if (me.autoActivate && !me.isActive) {
        me.activateQRCodeMode();
      }
      me.PushChanges();
    }
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("QRCodeDisplayComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /**
   * Generate QR code SVG from data string
   * @param data - String data to encode in QR code
   * @returns SafeHtml - Sanitized HTML containing QR code SVG
   */
  public generateQRCode(data: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(new QRCode(data).svg());
  }

  /**
   * Check if device is currently online
   * @returns boolean - true if online, false if offline
   */
  public getIsOnline(): boolean {
    return this._coreCOMService.environment.networkInformation.isOnline;
  }

  /**
   * Activate QR code mode - increases brightness and keeps screen on
   * Call this when QR code is displayed
   */
  public async activateQRCodeMode(): Promise<void> {
    const me = this;
    me.isActive = true;
    await me.setBrightness();
  }

  /**
   * Setup conditional brightness activation based on Observable trigger
   * Subscribes to the trigger$ Observable and activates brightness when it emits true
   * Uses take(1) to auto-unsubscribe after first true emission
   *
   * @param trigger$ - Observable that emits boolean values
   */
  public setupConditionalBrightness(trigger$: Observable<boolean>): void {
    const me = this;

    // Cleanup previous subscription if exists
    if (me.triggerSubscription) {
      me.triggerSubscription.unsubscribe();
    }

    me.triggerSubscription = trigger$
      .pipe(filter(Boolean), take(1) // Auto-complete after first emission
      ).subscribe(() => void me.activateQRCodeMode());
  }

  /**
   * Deactivate QR code mode - resets brightness and allows screen to sleep
   * Call this in ngOnDestroy or when QR code is hidden
   */
  public async deactivate(): Promise<void> {
    const me = this;
    me.isActive = false;
    await me.resetBrightness();
    me.cleanup();
  }

  /**
   * Set brightness to QR code level 
   * Only works on mobile devices
   */
  private async setBrightness(): Promise<void> {
    const me = this;
    if (me._coreCOMService.environment.client.isMobile) {
      try {
        await me._coreCOMService.environment.brightness.setBrightness(appConfig.brightness.qr_code);
        me._coreCOMService.environment.brightness.setKeepScreenOn(true);
      } catch (error) {
        me._coreLogService.error('QRCodeService.setBrightness', 'Failed to set brightness', error);
      }
    }
  }

  /**
   * Reset brightness to original value
   * Only works on mobile devices
   */
  private async resetBrightness(): Promise<void> {
    const me = this;
    if (me._coreCOMService.environment.client.isMobile) {
      try {
        await me._coreCOMService.environment.brightness.setBrightness(me.defaultBrightness);
        me._coreCOMService.environment.brightness.setKeepScreenOn(false);
      } catch (error) {
        me._coreLogService.error('QRCodeService.resetBrightness', 'Failed to reset brightness', error);
      }
    }
  }

  /**
   * Handle app pause event 
   * - reset brightness when app goes to background
   */
  private async onPause(): Promise<void> {
    const me = this;
    if (me.isActive) {
      await me.resetBrightness();
    }
  }

  /**
   * Handle app resume event 
   * - restore QR code brightness when app comes back
   */
  private async onResume(): Promise<void> {
    const me = this;
    if (me.isActive) {
      await me.setBrightness();
    }
  }

  /** Cleanup subscriptions */
  private cleanup(): void {
    const me = this;
    if (me.pauseSubscription) {
      me.pauseSubscription.unsubscribe();
      me.pauseSubscription = null;
    }
    if (me.resumeSubscription) {
      me.resumeSubscription.unsubscribe();
      me.resumeSubscription = null;
    }
    if (me.triggerSubscription) {
      me.triggerSubscription.unsubscribe();
      me.triggerSubscription = null;
    }
  }
}
